import { useState } from 'react';
import { Search, GraduationCap } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatCard } from '@/components/Card';
import { useApi } from '@/hooks/useApi';

interface Course {
  id: string;
  title: string;
  category: string;
  format: string;
  duration_minutes: number;
  mandatory: boolean;
  enrollment_count: number;
  completion_rate: number;
}

export function TrainingPage() {
  const [search, setSearch] = useState('');
  const { data, loading } = useApi<{ courses: Course[] }>(`training/courses?search=${search}`, [search]);

  const courses = data?.courses ?? [];
  const mandatory = courses.filter(c => c.mandatory);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total Courses" value={courses.length} icon={<GraduationCap className="w-5 h-5" />} accent />
        <StatCard label="Mandatory" value={mandatory.length} />
        <StatCard label="Avg Completion" value={courses.length > 0 ? `${Math.round(courses.reduce((a, c) => a + c.completion_rate, 0) / courses.length)}%` : '0%'} />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-2 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500" />
        </div>
      </div>

      {loading ? <div className="text-muted-foreground">Loading...</div> : (
        <DataTable
          columns={[
            { key: 'title', header: 'Course', render: (r: Course) => <span className="font-medium">{r.title}</span> },
            { key: 'category', header: 'Category', render: (r: Course) => <span className="capitalize">{r.category}</span> },
            { key: 'format', header: 'Format', render: (r: Course) => <span className="capitalize">{r.format.replace('_', ' ')}</span> },
            { key: 'duration_minutes', header: 'Duration', render: (r: Course) => `${r.duration_minutes}min` },
            { key: 'mandatory', header: 'Required', render: (r: Course) => r.mandatory ? <span className="text-amber-400 text-xs">Required</span> : <span className="text-muted-foreground text-xs">Optional</span> },
            { key: 'enrollment_count', header: 'Enrolled' },
            { key: 'completion_rate', header: 'Completion', render: (r: Course) => `${r.completion_rate}%` },
          ]}
          data={courses}
        />
      )}
    </div>
  );
}
